import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import DownloadIcon from '@mui/icons-material/Download';
import { exportRecordingsCSV, downloadBlob } from 'api/canvms';

function formatDuration(seconds) {
  if (!seconds) return '--:--';
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatFileSize(bytes) {
  if (!bytes) return '--';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatTime(isoString) {
  if (!isoString) return '--:--';
  const date = new Date(isoString);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function RecordingBrowser({
  recordings,
  selectedRecording,
  selectedDate,
  onDateChange,
  onRecordingSelect,
  loading,
  error,
  siteId,
  cameraId,
}) {
  const [exporting, setExporting] = useState(false);
  const dateValue = selectedDate ? selectedDate.toISOString().split('T')[0] : '';

  const handleDateChange = (e) => {
    const value = e.target.value;
    if (value) {
      onDateChange?.(new Date(value + 'T00:00:00'));
    }
  };

  const handleExportCSV = async () => {
    if (!siteId || !cameraId) return;

    try {
      setExporting(true);
      const startDate = selectedDate ? selectedDate.toISOString() : undefined;
      const endDate = selectedDate
        ? new Date(selectedDate.getTime() + 24 * 60 * 60 * 1000 - 1).toISOString()
        : undefined;
      const blob = await exportRecordingsCSV(siteId, cameraId, startDate, endDate);
      downloadBlob(blob, `recordings-${siteId}-${cameraId}-${Date.now()}.csv`);
    } catch (err) {
      console.error('[CANVMS/RecordingBrowser] Error exporting recordings:', err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Date picker */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          type="date"
          label="Select Date"
          value={dateValue}
          onChange={handleDateChange}
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {/* Recording list */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        ) : recordings.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <VideocamIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography color="text.secondary">
              No recordings found for this date
            </Typography>
          </Box>
        ) : (
          <List dense>
            {recordings.map((recording) => (
              <ListItem key={recording.filename} disablePadding>
                <ListItemButton
                  selected={selectedRecording?.filename === recording.filename}
                  onClick={() => onRecordingSelect?.(recording)}
                >
                  <ListItemText
                    primary={formatTime(recording.time)}
                    secondary={
                      <Box component="span" sx={{ display: 'flex', gap: 2 }}>
                        <span>Duration: {formatDuration(recording.duration)}</span>
                        <span>Size: {formatFileSize(recording.size)}</span>
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Summary — Export disabled pending VMS migration */}
      {!loading && recordings.length > 0 && (
        <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {recordings.length} recording{recordings.length !== 1 ? 's' : ''} found
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Recordings unavailable — Pending VMS migration
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default RecordingBrowser;
